import { z } from 'zod';
import { PROTOCOL_SECTIONS, ProtocolSchema } from './protocol';
/**
 * What the therapist sees about the client of a visit (owner 15.10.2026): the last treatments,
 * how many visits, notes and preferences — so a regular client gets the same good treatment
 * from any therapist. Never the phone or e-mail.
 */
/** From this many attended visits the client is shown as "regular". */
export const REGULAR_CLIENT_VISITS = 4;
/** How many past visits the card shows (and notes before "show more"). */
export const CARD_LAST_VISITS = 3;
/** Notes sent to the tablet at most (owner 20.10.2026: the whole history behind "show more"). */
export const CARD_NOTES_MAX = 60;
/** Internal note about the client written on the tablet (appended to the CRM card Note). */
export const CLIENT_NOTE_TEXT_MAX = 500;
/** How many protocols the card sends at most. */
export const CARD_PROTOCOLS_MAX = 30;
/**
 * A paper protocol typed in later (owner 04.11.2026: «поле дата визита, чтобы переносить бумажные
 * протоколы»). Kept in the client's card only: never e-mailed or pushed.
 */
export const PastProtocolRequestSchema = z
    .object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    staffName: z.string().trim().max(60).optional(),
    protocol: ProtocolSchema,
    text: z.string().trim().max(1000).default(''),
    nextInWeeks: z.number().int().min(1).max(26).optional(),
})
    .refine((r) => r.date >= '2015-01-01', { path: ['date'], message: 'date too early' })
    .refine((r) => r.text.length >= 3 || r.protocol.picked.length > 0 || Boolean(r.protocol.other), {
    path: ['protocol'],
    message: 'pick something or write a comment',
});
/** Card from the CRM visits (newest first), Hub notes and form preferences. */
export function buildClientCard(input) {
    const list = Array.isArray(input.visits)
        ? [...input.visits].filter((v) => v.date < input.today).sort((a, b) => b.date.localeCompare(a.date))
        : [];
    const crmNotes = list
        .filter((v) => v.comment && v.comment.trim().length > 1)
        .map((v) => ({ date: v.date, text: v.comment.trim(), source: 'crm', staffName: v.staffName }));
    const notes = [...input.therapistNotes, ...crmNotes]
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, CARD_NOTES_MAX);
    const clientNote = input.clientNote?.trim();
    return {
        totalVisits: list.length,
        totalIsMinimum: Boolean(input.capped),
        ...(list.length ? { firstVisit: list[list.length - 1].date } : {}),
        regular: list.length >= REGULAR_CLIENT_VISITS,
        lastVisits: list.slice(0, CARD_LAST_VISITS).map(({ comment: _c, ...v }) => {
            void _c;
            return v;
        }),
        notes,
        ...(clientNote ? { clientNote } : {}),
        canAddNote: Boolean(input.canAddNote),
        ...(input.prefs ? { prefs: input.prefs } : {}),
        history: Array.isArray(input.visits) ? 'crm' : input.visits,
        protocols: [...(input.protocols ?? [])].sort((a, b) => b.date.localeCompare(a.date)).slice(0, CARD_PROTOCOLS_MAX),
    };
}
/**
 * Internal note about the client for the next therapists (owner 20.10.2026). Goes to the end of the
 * CRM card Note as one line `[Hub dd.mm.yyyy Name] text`; the existing Note is never changed.
 * Not e-mailed, not shown to the client.
 */
export const ClientNoteRequestSchema = z.object({
    text: z
        .string()
        .trim()
        .min(3)
        .max(CLIENT_NOTE_TEXT_MAX)
        // one line in the Note: no control characters, newlines become "; "
        // eslint-disable-next-line no-control-regex
        .transform((t) => t
        .replace(/[\r\n]+/g, '; ')
        .replace(/[\u0000-\u001f\u007f]/g, ' ')
        .replace(/\s{2,}/g, ' ')),
});
/** The line Hub appends to the CRM Note. `date` is the local YYYY-MM-DD. */
export function clientNoteLine(date, staffName, text) {
    const [y, m, d] = date.split('-');
    const who = staffName.replace(/[[\]]/g, '').trim() || 'Hub';
    return `[Hub ${d}.${m}.${y} ${who}] ${text}`;
}
/** Existing Note + the new line (a blank Note becomes just the line). */
export function appendNoteLine(existing, line) {
    const base = (existing ?? '').replace(/\s+$/, '');
    return base ? `${base}\n${line}` : line;
}
// ───────────── recommendations written by the therapist, e-mailed to the client ─────────────
export const RECOMMENDATION_TEXT_MAX = 1000;
const CLIENT_PROTOCOL_IDS = new Set(PROTOCOL_SECTIONS.filter((x) => x.client).flatMap((x) => x.options.map((o) => o.id)));
export const RecommendationRequestSchema = z
    .object({
    /** The therapist's own words; may be empty when the protocol says enough (owner 2.11.2026). */
    text: z.string().trim().max(RECOMMENDATION_TEXT_MAX).default(''),
    /** Treatment protocol picked from lists (owner 2.11.2026); the studio keeps it whole. */
    protocol: ProtocolSchema.optional(),
    /** Products for home care (ids `p-…`), shown with their names in the e-mail. */
    productIds: z
        .array(z.string().regex(/^p-[a-z0-9-]{1,37}$/))
        .max(5)
        .default([]),
    /** "Come back in N weeks". */
    nextInWeeks: z.number().int().min(1).max(26).optional(),
    /** false = keep as a note only, do not e-mail. */
    send: z.boolean().default(true),
})
    .refine((r) => r.text.length >= 3 ||
    r.productIds.length > 0 ||
    (r.protocol?.picked ?? []).some((id) => CLIENT_PROTOCOL_IDS.has(id)), { path: ['text'], message: 'write a recommendation or pick something for the client' });
//# sourceMappingURL=client-card.js.map